import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { styled } from '@components-shared/src/stitches';
import { CollectButton } from '@components/CollectButton';
import { EmptySeating } from '@components/icons/Release/emptySeating';
import { Button } from '@components/main-components/button/Button';
import { CheckoutModal } from '@components/modals/Buy/Checkout/CheckoutModal';
import { ModalType, useModal } from '@context/modal';
import type { EmptySeatingModalInfoFragment, FragmentType } from '@graphql/reactQuery';
import { EmptySeatingModalInfoFragmentDoc, getFragment } from '@graphql/reactQuery';
import { useIsUploadPreview } from '@hooks/useIsUploadPreview';
import { useEditionCollectableSchedules } from '@store/sdk/reactQuery';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment EmptySeatingModalInfo on Release {
    id
    ...EditionCollectableSchedulesInfo
    ...CheckoutReleaseInfo
  }
`);

export const EmptySeatingPreview = () => {
  return (
    <Container withPadding>
      <EmptySeating />
      <p>Be the first one to join the audience for this song</p>
      <Button variant="primary" label="Collect" size="M" disabled fullWidth />
    </Container>
  );
};

const EmptySeatingSongPage = ({
  releaseId,
  releaseFrag,
}: {
  releaseId: string;
  releaseFrag: EmptySeatingModalInfoFragment;
}) => {
  const { openModal } = useModal();

  const { firstCollectableScheduleWithSaleAccess: collectableSchedule, queriesResult } =
    useEditionCollectableSchedules({
      release: releaseFrag,
    });

  const collectingEnabled = !!collectableSchedule;

  const renderButton = useStableCallback(() => {
    return (
      <CollectButton
        mintType="Fixed"
        releaseId={releaseId}
        sourceComponent="Audience"
        onBuy={() => {
          if (!collectableSchedule) return;

          // We are just handling new contract buys
          // Since we no longer have any old buys to support
          openModal(ModalType.RELEASE_CHECKOUT, {
            body: (
              <CheckoutModal
                releaseData={releaseFrag}
                schedule={collectableSchedule}
                headingText="Fixed"
                selectedPanel={collectableSchedule.mintType === 'MerkleDrop' ? 'PRESALE' : 'PUBLIC'}
              />
            ),
          });
        }}
        mintStarted={collectingEnabled}
        soldOut={false}
        hasSaleAccess={collectingEnabled}
        areSchedulesLoading={queriesResult.some(result => result.isInitialLoading)}
      />
    );
  });

  return (
    <Container>
      <EmptySeating />
      <p>Be the first one to join the audience for this song</p>
      {renderButton()}
    </Container>
  );
};

export const EmptySeatingModal = ({
  release,
}: {
  release: FragmentType<EmptySeatingModalInfoFragmentDoc>;
}) => {
  const isPreview = useIsUploadPreview();
  const releaseFrag = getFragment(EmptySeatingModalInfoFragmentDoc, release);

  const releaseId = releaseFrag.id;

  return (
    <Container>
      {isPreview ? (
        <EmptySeatingPreview />
      ) : (
        <EmptySeatingSongPage releaseId={releaseId} releaseFrag={releaseFrag} />
      )}
    </Container>
  );
};

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: 297,
  height: 255,
  background: '#fff',
  lift: 'tertiary',
  borderRadius: 8,
  zIndex: '$above2',
  svg: {
    width: 141,
  },

  p: {
    base: 'M',
    fontStyle: 'semibold',
    textAlign: 'center',
    maxWidth: 180,
    margin: '8px 0 19px 0',
  },

  '@tablet': {
    width: 393,
    height: 272,

    svg: {
      width: 206,
    },

    p: {
      maxWidth: 'unset',
      marginTop: 19,
    },
  },

  variants: {
    withPadding: {
      true: {
        '@tablet': {
          padding: 32,
        },
      },
    },
  },
});
